import { addDays, subDays } from 'date-fns';
import { localeFromLang, type Language } from '../../../src/api/langConfig';
import { CHECKOUT_DEADLINE_URGENCY_MS } from '../api/constants';
import { ORDER_DEADLINE_TIMEZONE, toDateFromServer } from '../api/dates';
import type { ModelComplexity, Order } from '../types';
import {
    canOrderMenuMealsForDay,
    getCheckoutDayAllowedResult,
    getOrderSetMenuMealLinesByLineId,
    isGeneralOrderDeadlinePassed,
    type CheckoutDayBlockCategory,
    type OrderDayDeadlineContext,
} from './orderDeadlines';

export type CheckoutDeadlineKind = 'general' | 'set_menu';

export type CheckoutDeadlineInstant = { at: Date; kind: CheckoutDeadlineKind };

const MINUTE_MS = 60 * 1000;

function blockCategoryToKind(category: CheckoutDayBlockCategory): CheckoutDeadlineKind {
    return category === 'set_menu_deadline' ? 'set_menu' : 'general';
}

/** First `at` (minute resolution) when `predicate(at)` becomes true scanning forward from `before`. */
function findFirstMinuteWhen(
    before: Date,
    searchEnd: Date,
    searchStart: Date,
    predicate: (at: Date) => boolean,
): Date | null {
    if (predicate(before)) {
        return null;
    }
    let lo = before.getTime();
    let hi = Math.min(searchEnd.getTime(), before.getTime() + 120 * 24 * 60 * MINUTE_MS);
    const start = Math.max(searchStart.getTime(), before.getTime());
    if (lo < start) {
        lo = start;
    }
    if (predicate(new Date(hi))) {
        while (hi - lo > MINUTE_MS) {
            const mid = lo + Math.floor((hi - lo) / (2 * MINUTE_MS)) * MINUTE_MS;
            if (predicate(new Date(mid))) {
                hi = mid;
            } else {
                lo = mid;
            }
        }
        for (let t = lo + MINUTE_MS; t <= hi; t += MINUTE_MS) {
            if (predicate(new Date(t))) {
                return new Date(t);
            }
        }
        return new Date(hi);
    }
    for (let t = before.getTime() + MINUTE_MS; t <= searchEnd.getTime(); t += MINUTE_MS) {
        if (predicate(new Date(t))) {
            return new Date(t);
        }
    }
    return null;
}

/** When general cancel/change / new-order deadline closes for `orderDate` (matches `isGeneralOrderDeadlinePassed`). */
export function getGeneralCheckoutDeadlineInstant(
    orderDate: string,
    modelConstants: ModelComplexity | null | undefined,
    referenceBefore: Date = new Date(),
): Date | null {
    const target = toDateFromServer(orderDate);
    const orderDays = modelConstants?.orderDays ?? 1;
    const searchStart = subDays(target, orderDays + 14);
    const searchEnd = addDays(target, 1);
    return findFirstMinuteWhen(
        referenceBefore,
        searchEnd,
        searchStart,
        at => isGeneralOrderDeadlinePassed(orderDate, modelConstants, at),
    );
}

/** When set-menu ordering closes for `orderDate` (matches `canOrderMenuMealsForDay`). */
export function getSetMenuCheckoutDeadlineInstant(
    orderDate: string,
    modelConstants: ModelComplexity | null | undefined,
    referenceBefore: Date = new Date(),
): Date | null {
    const target = toDateFromServer(orderDate);
    const orderDays = modelConstants?.orderMenuDays ?? 1;
    const searchStart = subDays(target, orderDays + 21);
    const searchEnd = addDays(target, 1);
    return findFirstMinuteWhen(
        referenceBefore,
        searchEnd,
        searchStart,
        at => !canOrderMenuMealsForDay(orderDate, modelConstants, at),
    );
}

/** Cutoff instant for a stale row (deadline already passed). */
export function getStaleCheckoutDeadlineInstant(
    orderDate: string,
    category: CheckoutDayBlockCategory,
    modelConstants: ModelComplexity | null | undefined,
): Date | null {
    const referenceBefore = subDays(toDateFromServer(orderDate), 90);
    if (category === 'set_menu_deadline') {
        return getSetMenuCheckoutDeadlineInstant(orderDate, modelConstants, referenceBefore);
    }
    return getGeneralCheckoutDeadlineInstant(orderDate, modelConstants, referenceBefore);
}

/** User-facing cutoff date/time in school deadline timezone. */
export function formatCheckoutDeadlineAt(instant: Date, lang: Language): string {
    return instant.toLocaleString(localeFromLang(lang, 'dateTime'), {
        timeZone: ORDER_DEADLINE_TIMEZONE,
        day: 'numeric',
        month: 'long',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });
}

/**
 * Next checkout cutoff for urgency — general order deadline wins when it closes with or before set-menu.
 */
export function getEarliestCheckoutDeadlineInstantForEntry(
    orderDate: string,
    draft: Order,
    placed: Order | null | undefined,
    ctx: OrderDayDeadlineContext,
    userLang: Language,
    at: Date,
): CheckoutDeadlineInstant | null {
    if (getCheckoutDayAllowedResult(orderDate, draft, placed, ctx, userLang, at).ok === false) {
        return null;
    }

    const referenceBefore = new Date(at.getTime() - 1);
    const generalAt = getGeneralCheckoutDeadlineInstant(orderDate, ctx.modelConstants, referenceBefore);
    const setMenuLines = getOrderSetMenuMealLinesByLineId(draft, ctx.menuMealsCatalog, ctx.menuMealsForOrderDate);
    const setMenuAt = setMenuLines.length > 0
        ? getSetMenuCheckoutDeadlineInstant(orderDate, ctx.modelConstants, referenceBefore)
        : null;

    let chosenAt: Date | null = null;
    let kind: CheckoutDeadlineKind = 'general';

    if (generalAt && setMenuAt) {
        if (generalAt.getTime() <= setMenuAt.getTime()) {
            chosenAt = generalAt;
            kind = 'general';
        } else {
            chosenAt = setMenuAt;
            kind = 'set_menu';
        }
    } else if (generalAt) {
        chosenAt = generalAt;
    } else if (setMenuAt) {
        chosenAt = setMenuAt;
        kind = 'set_menu';
    }

    if (!chosenAt || chosenAt.getTime() <= at.getTime()) {
        return null;
    }

    return { at: chosenAt, kind };
}

export type BasketCheckoutUrgencyEntry = {
    date: string;
    draft: Order;
    placed: Order | null;
};

export type BasketCheckoutUrgencyState =
    | { active: false }
    | { active: true; msUntilEarliest: number; earliest: CheckoutDeadlineInstant; orderDates: string[] };

export function getBasketCheckoutUrgency(
    entries: BasketCheckoutUrgencyEntry[],
    ctx: OrderDayDeadlineContext,
    at: Date,
    userLang: Language,
    windowMs: number = CHECKOUT_DEADLINE_URGENCY_MS,
): BasketCheckoutUrgencyState {
    let best: CheckoutDeadlineInstant | null = null;
    let bestMs = Infinity;

    for (const entry of entries) {
        const instant = getEarliestCheckoutDeadlineInstantForEntry(
            entry.date,
            entry.draft,
            entry.placed,
            ctx,
            userLang,
            at,
        );
        if (!instant) {
            continue;
        }
        const ms = instant.at.getTime() - at.getTime();
        if (ms <= 0 || ms > windowMs) {
            continue;
        }
        if (ms < bestMs) {
            bestMs = ms;
            best = instant;
        }
    }

    if (!best) {
        return { active: false };
    }

    const orderDates = entries
        .filter(entry => {
            const instant = getEarliestCheckoutDeadlineInstantForEntry(
                entry.date,
                entry.draft,
                entry.placed,
                ctx,
                userLang,
                at,
            );
            return instant && instant.at.getTime() === best!.at.getTime();
        })
        .map(e => e.date)
        .sort();

    return {
        active: true,
        msUntilEarliest: bestMs,
        earliest: best,
        orderDates,
    };
}

/** Closing hour (0–23 Athens) for urgency copy — set-menu vs general. */
export function checkoutDeadlineClosingHour(
    kind: CheckoutDeadlineKind,
    modelConstants: ModelComplexity | null | undefined,
): number {
    if (kind === 'set_menu') {
        const menuTime = modelConstants?.orderMenuTime;
        if (typeof menuTime === 'number' && Number.isFinite(menuTime)) {
            return menuTime;
        }
    }
    return modelConstants?.orderTime ?? 22;
}

export function formatCheckoutDeadlineHourLabel(hour: number): string {
    return `${String(hour).padStart(2, '0')}:00`;
}
